

import { CSSProperties } from "react";
import classes from "./PageWrapper.module.css";

import AllCategoriesSection from "@/containers/AllCategoriesSection";
import BannersSlider from "@/containers/BannersSlider";
import FeaturedProducts from "@/containers/FeaturedProducts";
import FilterProducts from "@/containers/FilterProducts";
import HeroSection from "@/containers/HeroSection";
import PopularProducts from "@/containers/PopularProducts";
import ProductSliderSection from "@/containers/ProductSliderSection";
import ProductWithThreeBanner from "@/containers/ProductWithThreeBanner";
import RandomBanners from "@/containers/RandomBanners";
import Services from "@/containers/Services";
import ShowBannersSection from "@/containers/ShowBannersSection";
import SmallWideBanner from "@/containers/ShowBannersSection/SmallWideBanners";
import ShowBannersSlider from "@/containers/ShowBannersSlider";
import TrendingProducts from "@/containers/TrendingProducts";
import { getPageRoute } from "@/utils/routes";
import SectionHeader from "../SectionHeader";

import { getSectionStyle } from "@/utils";
import { prefetchWidgets, type WidgetPrefetch } from "@/lib/widget-products";
import OfferTimeout from "@/containers/OfferTimeout";
import HeroSectionNewVersion from "@/containers/HeroSectionNewVersion";

interface Props {
  className?: string;
  style?: CSSProperties;

  page?: BuilderPage;
}

export const getBuilderElement = (
  widget: Widget,
  index: number | string,
  prefetched?: WidgetPrefetch,
) => {
  switch (widget.name) {
    case "title":
      return (
        // <div
        // 	key={index}
        // 	className="container"
        // 	style={getSectionStyle(widget?.data?.settings || {})}>
        // </div>
        <SectionHeader
          key={index + "-section-header"+widget.name}
          title={widget.title || ""}
          align={!!widget.link?.url ? "left" : "center"}
          link={
            widget?.link?.urlType
              ? getPageRoute(widget.link?.urlType, widget.link?.url || "")
              : ""
          }
          timeout={widget.data?.showTimer || false}
          btnText={widget.link?.hyperLinkText || ""}
          settings={widget?.data?.settings || {}}
        />
      );

    case "slider":
      return (
        <HeroSection
          key={index + "-hero-section-"+widget.name}
          images={[]}
          settings={{
            containerType: "full",
          }}
          widget={widget}
        />
      );
    case "hero-section":
      return (
        <HeroSectionNewVersion
          key={index + "-hero-section-"+widget.name}
          // images={[]}
          // settings={{
          //   containerType: "full",
          // }}
          widget={widget}
        />
      );

    case "categories":
      return (
        <AllCategoriesSection
          key={index + "-all-categories-section-"+widget.name}
          sectionHeader={{
            title: "All Categories",
            align: "center",
          }}
          widget={widget}
        />
      );

    case "product-slider":
      return <FeaturedProducts key={index + "-featured-products-"+widget.name} widget={widget} />;

    case "banners":
      const settings = widget?.data?.settings || {};

      if (settings?.isSlider) {
        return <ShowBannersSlider key={index + "-show-banners-slider-"+widget.name} widget={widget} />;
      }

      return <ShowBannersSection key={index + "-show-banners-section-"+widget.name} widget={widget} />;

    case "small-wide-banner":
      return <SmallWideBanner key={index + "-small-wide-banner-"+widget.name} widget={widget} />;

    case "trending-products":
      return <TrendingProducts key={index + "-trending-products-"+widget.name} widget={widget} initialLeftProducts={prefetched?.left} initialMainProducts={prefetched?.main} />;

    case "filter-products":
      return <FilterProducts key={index + "-filter-products-"+widget.name} widget={widget} initialProducts={prefetched?.products} />;

    case "three-banner-products":
      return <ProductWithThreeBanner key={index + "-three-banner-products-"+widget.name} widget={widget} initialProducts={prefetched?.products} />;

    case "new-arrival":
      return <ProductWithThreeBanner key={index + "-new-arrival-"+widget.name} />;

    case "brand-list":
      return <BannersSlider key={index + "-brand-list-"+widget.name} widget={widget} />;

    case "populer-product":
      return <PopularProducts key={index + "-populer-product-"+widget.name} widget={widget} initialProducts={prefetched?.products} />;

    case "small-wide-banner":
      return <RandomBanners key={index + "-small-wide-banner-"+widget.name} />;

    case "random-size-banners":
      return <RandomBanners key={index + "-random-size-banners-"+widget.name} widget={widget} />;

    case "single-product":
      return <ProductSliderSection key={index + "-single-product-"+widget.name} widget={widget} initialProducts={prefetched?.products} />;

    case "services":
      return <Services key={index + "-services-"+widget.name} widget={widget} />;

    case "padding":
      return (
        <div
          key={index + "-padding-"+widget.name}
          style={{
            ...getSectionStyle((widget as any).data?.settings),
            margin: `${(widget.data as any)?.padding || 0}px 0`,
          }}
        ></div>
      );

    case "content-editor":
      return (
        <div className="container pb-4" key={index + "-content-editor-"+widget.name}>
          <div
            className="html-content"
            dangerouslySetInnerHTML={{
              __html: widget?.data?.content || "",
            }}
          ></div>
        </div>
      );
    case "timeout":
      return (
        <OfferTimeout
          key={index + "-timeout-"+widget.name}
          endDate={(widget.data as any)?.timeoutDate}
          title={(widget.data as any)?.countDownTitle}
        />
      );
  }
};

const PageWrapper = async ({
  className,
  style,

  page,
}: Props) => {
  const classNames = [classes.PageWrapper, className];

  const widgets = page?.widgets || [];

  // Server-side prefetch: product data for every widget is fetched here (in
  // parallel, cached 5 min) so it ships inside the initial HTML instead of
  // being fetched from the browser after hydration.
  const prefetched = await prefetchWidgets(widgets);

  return (
    <div className={classNames.join(" ")} style={style}>
      {widgets.map((widget, index) => {
        return getBuilderElement(widget, index, prefetched[index]);
      })}
    </div>
  );
};

/**
 * Sync variant for CLIENT contexts (e.g. category topbar) that can't render
 * the async server PageWrapper. No server prefetch — widgets fetch their own
 * data client-side, as before.
 */
export const PageWrapperSync = ({ className, style, page }: Props) => {
  const classNames = [classes.PageWrapper, className];
  const widgets = page?.widgets || [];

  return (
    <div className={classNames.join(" ")} style={style}>
      {widgets.map((widget, index) => getBuilderElement(widget, index))}
    </div>
  );
};

export default PageWrapper;
